import { Pipe, PipeTransform } from '@angular/core';

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'SPAN']);
const DROPPED_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH']);

/**
 * Keeps simple formatting (paragraphs, lists, bold/italic, line breaks) from text supplied by Business Central
 * and removes everything else: other tags are unwrapped to their text, dangerous ones are dropped with their content,
 * and every attribute is stripped. The result is still passed through Angular's own sanitizer by [innerHTML].
 */
@Pipe({ name: 'safeHtml' })
export class SafeHtmlPipe implements PipeTransform {
  transform(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    // DOMParser documents are inert: nothing is loaded or executed while parsing.
    const source = new DOMParser().parseFromString(value, 'text/html').body;
    const target = document.implementation.createHTMLDocument('').body;
    SafeHtmlPipe.copyAllowed(source, target);

    return target.innerHTML;
  }

  private static copyAllowed(from: Node, to: Node): void {
    for (const child of Array.from(from.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        to.appendChild(to.ownerDocument!.createTextNode(child.textContent ?? ''));
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = (child as Element).tagName.toUpperCase();
        if (DROPPED_WITH_CONTENT.has(tag)) {
          continue;
        }

        if (ALLOWED_TAGS.has(tag)) {
          const element = to.ownerDocument!.createElement(tag);
          to.appendChild(element);
          SafeHtmlPipe.copyAllowed(child, element);
        } else {
          SafeHtmlPipe.copyAllowed(child, to);
        }
      }
    }
  }
}
